// Helper functions to standardize Medical Chronology analytics tracking
// All functions hide boilerplate such as suppressing property inheritance
// and common Medical-Chronology-specific payload fields.

import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'

/**
 * Track a Medical Chronology specific analytics event.
 * Automatically applies { suppressFeatureInheritance: true } so that no
 * residual properties from previous events leak into this payload.
 */
export function trackMedChronEvent(
  event: AnalyticsEvent,
  props: Record<string, string | number | boolean>,
  track: TrackEventFn | undefined = getTrackEventGlobal()
) {
  if (!track) return
  track(event, props, {
    suppressFeatureInheritance: true,
  })
}

/**
 * Convenience helper that fires ArtifactDownloadSuccess / ArtifactDownloadFailed
 * with the Medical Chronology context attached (isMedChronArtifact + jobId).
 *
 * @param success   Whether the download succeeded.
 * @param artifactId The artifact ID.
 * @param artifactVersionId The artifact version ID.
 * @param jobId     Associated Medical Chronology job ID.
 * @param error     Optional error message (only for failures).
 */
export function trackMedChronArtifactDownload(success: boolean, artifactId: string, artifactVersionId: string, jobId: string, error?: string) {
  const track = getTrackEventGlobal()
  if (!track) return

  if (success) {
    track(AnalyticsEvent.ArtifactDownloadSuccess, {
      artifactId,
      artifactVersionId,
      isMedChronArtifact: true,
      medChronJobId: jobId,
    })
  } else {
    track(AnalyticsEvent.ArtifactDownloadFailed, {
      artifactId,
      artifactVersionId,
      errorMessage: error ?? 'Unknown error',
      isMedChronArtifact: true,
      medChronJobId: jobId,
    })
  }
}
